import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Target, Users, FileCheck, FileText, Sparkles, Zap, Shield, TrendingUp } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const SalesAgentLibrary = () => {
  const navigate = useNavigate();
  const [agentsRef, agentsVisible] = useScrollAnimation({ threshold: 0.1 });
  const [reuseRef, reuseVisible] = useScrollAnimation({ threshold: 0.1 });

  const agents = [
    {
      id: 1,
      title: 'Lead Qualification Scoring Agent',
      description: 'Auto-scores inbound leads by enriching firmographics and intent, filtering spam, and prioritizing high-value prospects so reps focus where conversion odds are highest.',
      icon: <Target className="w-8 h-8" />,
      color: 'from-blue-500/20 to-cyan-500/20',
      iconColor: 'text-cyan-400',
      features: ['Firmographic enrichment', 'Intent scoring', 'Spam filtering', 'Priority ranking']
    },
    {
      id: 2,
      title: 'Lead Assignment Agent',
      description: 'Routes each qualified lead to the best-fit rep/team using territory, expertise, and workload rules, then pings them instantly across email/Slack for fastest follow-up.',
      icon: <Users className="w-8 h-8" />,
      color: 'from-purple-500/20 to-pink-500/20',
      iconColor: 'text-pink-400',
      features: ['Territory routing', 'Expertise matching', 'Workload balancing', 'Instant notifications']
    },
    {
      id: 3,
      title: 'Sales Order Creation & Validation Agent',
      description: 'Converts "closed-won" deals into clean OMS orders, validating SKUs, pricing, terms, and required fields to prevent errors and speed fulfillment.',
      icon: <FileCheck className="w-8 h-8" />,
      color: 'from-green-500/20 to-emerald-500/20',
      iconColor: 'text-emerald-400',
      features: ['SKU validation', 'Pricing checks', 'Terms verification', 'Error prevention']
    },
    {
      id: 4,
      title: 'Quote Generation Agent',
      description: 'Generates policy-compliant quotes with rule-based pricing and approvals, adds smart upsell/cross-sell, and returns a polished, traceable PDF to CRM.',
      icon: <FileText className="w-8 h-8" />,
      color: 'from-orange-500/20 to-amber-500/20',
      iconColor: 'text-amber-400',
      features: ['Rule-based pricing', 'Smart upsell', 'PDF generation', 'CRM integration']
    }
  ];

  const handleBookDemo = () => {
    window.open('https://calendly.com/speakmulti/introduction', '_blank');
  };

  const handleHowItWorks = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('how-oneplace-works');
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#12D7E8]/10 via-transparent to-purple-500/10 animate-gradient-shift" />
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(18, 215, 232, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(18, 215, 232, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        <div className="light-full-container relative z-10">
          <div className="light-content-container">
            <div className="max-w-4xl mx-auto text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#12D7E8]/10 border border-[#12D7E8]/20 mb-6">
                <Sparkles className="w-4 h-4 text-[#12D7E8]" />
                <span className="text-sm font-semibold text-[#12D7E8]">Sales Agent Library</span>
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-white to-[#12D7E8] bg-clip-text text-transparent">
                Accelerate Sales with Intelligent Agent Workflows
              </h1>

              {/* Subtitle */}
              <p className="text-xl text-[rgba(255,255,255,0.7)] mb-10 leading-relaxed max-w-3xl mx-auto">
                Automate qualification, assignment, quote generation, and order validation — all within OnePlace's unified agentic platform.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button 
                  onClick={handleBookDemo}
                  className="group px-8 py-4 bg-[#12D7E8] text-black font-semibold rounded-lg hover:bg-[#33E1FF] transition-all duration-300 flex items-center gap-2 shadow-lg shadow-[#12D7E8]/20 hover:shadow-[#12D7E8]/40 hover:scale-105"
                >
                  Book a Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 mt-16 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#12D7E8] mb-2">4</div>
                  <div className="text-sm text-[rgba(255,255,255,0.6)]">Sales Agents</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#12D7E8] mb-2">10x</div>
                  <div className="text-sm text-[rgba(255,255,255,0.6)]">Faster Setup</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#12D7E8] mb-2">100%</div>
                  <div className="text-sm text-[rgba(255,255,255,0.6)]">Traceable</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agents Grid Section */}
      <section ref={agentsRef} className="section-spacing relative">
        <div className="light-full-container">
          <div className="light-content-container">
            <div className="max-w-7xl mx-auto">
              {/* Section Header */}
              <div className="text-center mb-16">
                <h2 className="display-large mb-4">Meet Your Sales <span className="text-[#12D7E8]">Agent Team</span></h2>
                <p className="text-lg text-[rgba(255,255,255,0.7)] max-w-2xl mx-auto">
                  Four specialized agents working in perfect harmony to supercharge your sales pipeline
                </p>
              </div>

              {/* Agents Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {agents.map((agent, index) => (
                  <div
                    key={agent.id}
                    onClick={() => {
                      if (agent.id === 1) navigate('/agent-library/sales/lead-qualification');
                      if (agent.id === 2) navigate('/agent-library/sales/lead-assignment');
                      if (agent.id === 3) navigate('/agent-library/sales/sales-order-creation');
                      if (agent.id === 4) navigate('/agent-library/sales/quote-generation');
                    }}
                    className={`agent-library-card group ${agentsVisible ? 'animate-fade-in-up' : 'opacity-0'} cursor-pointer`}
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    {/* Gradient background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${agent.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
                    
                    <div className="relative z-10">
                      {/* Icon */}
                      <div className={`inline-flex p-4 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] mb-6 ${agent.iconColor} group-hover:scale-110 transition-transform duration-300`}>
                        {agent.icon}
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-[#12D7E8] transition-colors">
                        {agent.title}
                      </h3>

                      {/* Description */}
                      <p className="text-[rgba(255,255,255,0.7)] leading-relaxed mb-6">
                        {agent.description}
                      </p>

                      {/* Features */}
                      <div className="grid grid-cols-2 gap-3">
                        {agent.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <Zap className="w-4 h-4 text-[#12D7E8] flex-shrink-0" />
                            <span className="text-[rgba(255,255,255,0.6)]">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Hover indicator */}
                      <div className="flex items-center gap-2 mt-6 text-[#12D7E8] opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-sm font-semibold">Learn more</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Build Once, Reuse Everywhere Section */}
      <section ref={reuseRef} className="section-spacing relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#12D7E8]/5 to-transparent" />
        
        <div className="light-full-container relative z-10">
          <div className="light-content-container">
            <div className="max-w-6xl mx-auto">
              <div className={`relative bg-gradient-to-br from-[rgba(255,255,255,0.03)] to-[rgba(255,255,255,0.01)] border border-[rgba(255,255,255,0.1)] rounded-3xl p-12 md:p-16 ${reuseVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#12D7E8]/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  {/* Icons row */}
                  <div className="flex justify-center gap-4 mb-8">
                    <div className="p-3 rounded-xl bg-[#12D7E8]/10 border border-[#12D7E8]/20">
                      <TrendingUp className="w-6 h-6 text-[#12D7E8]" />
                    </div>
                    <div className="p-3 rounded-xl bg-[#12D7E8]/10 border border-[#12D7E8]/20">
                      <Shield className="w-6 h-6 text-[#12D7E8]" />
                    </div>
                    <div className="p-3 rounded-xl bg-[#12D7E8]/10 border border-[#12D7E8]/20">
                      <Zap className="w-6 h-6 text-[#12D7E8]" />
                    </div>
                  </div>

                  <h2 className="text-4xl md:text-5xl font-bold text-center mb-6">
                    Build once. <span className="text-[#12D7E8]">Reuse everywhere.</span>
                  </h2>

                  <p className="text-xl text-[rgba(255,255,255,0.7)] text-center leading-relaxed mb-10 max-w-3xl mx-auto">
                    Start with a template, connect your CRM and data sources, and tailor rules to your motion. Each agent runs with the same observability, governance, and security you've seen across the platform—so pilots graduate to production without rewrites.
                  </p>

                  {/* Features grid */}
                  <div className="grid md:grid-cols-3 gap-6 mb-10">
                    <div className="text-center p-6 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)]">
                      <div className="text-3xl mb-3">🎯</div>
                      <h4 className="font-semibold mb-2 text-white">Template-First</h4>
                      <p className="text-sm text-[rgba(255,255,255,0.6)]">Start fast with proven patterns</p>
                    </div>
                    <div className="text-center p-6 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)]">
                      <div className="text-3xl mb-3">🔗</div>
                      <h4 className="font-semibold mb-2 text-white">Plug & Play</h4>
                      <p className="text-sm text-[rgba(255,255,255,0.6)]">Connect to your existing stack</p>
                    </div>
                    <div className="text-center p-6 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)]">
                      <div className="text-3xl mb-3">🚀</div>
                      <h4 className="font-semibold mb-2 text-white">Production-Ready</h4>
                      <p className="text-sm text-[rgba(255,255,255,0.6)]">No rewrites needed</p>
                    </div>
                  </div>

                  <div className="text-center">
                    <button 
                      onClick={handleHowItWorks}
                      className="group inline-flex items-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white font-semibold rounded-lg hover:bg-[rgba(255,255,255,0.1)] hover:border-[#12D7E8]/50 transition-all duration-300"
                    >
                      Know More — How OnePlace Works
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        @keyframes gradient-shift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, 20px); }
        }

        .animate-gradient-shift {
          animation: gradient-shift 8s ease-in-out infinite;
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .agent-library-card {
          position: relative;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1rem;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .agent-library-card:hover {
          transform: translateY(-4px);
          border-color: rgba(18, 215, 232, 0.3);
          box-shadow: 0 20px 60px rgba(18, 215, 232, 0.1);
        }
      `}</style>
    </div>
  );
};

export default SalesAgentLibrary;
